import {useEffect, useContext} from 'react';
import {useSelector, useDispatch, useStore} from 'react-redux';
import {useRouter} from '@/common/services/router';
import {useUrlBuilder} from '@/app/routing/useUrlBuilder';
import {ServicesContext} from '@/app/contexts/ServicesContext';
import {RouteName} from '@/app/constants/routing';
import {LocationPoint} from '@/common/interfaces/location';
import {StoreState, StoreThunkDispatch} from '@/app/store';
import {selectLanguage} from '@/app/store/config/selectors';
import {reFetchForecastDataAction} from '@/app/store/forecast/actions';
import {selectForecastData, selectForecastViewParams} from '@/app/store/forecast/selectors';
import config from '@config.client';

const refreshCurrentInterval = config.currentWeatherRefreshInterval;
const refreshInactiveTabPeriod = config.forecastUpdateInactiveTabPeriod;

const fullRefreshAt = config.forecastRefreshAtMinutes
	.slice()
	.map(({min, max}) => {
		return Math.round((min + (max - min + 1) * Math.random()) * 60 * 1000);
	})
	.sort((a, b) => (a > b ? 1 : -1));

/**
 * The hook provides the logic to periodically update forecast and current weather data at the required intervals
 */
export function useWeatherActualize(location: LocationPoint) {
	const profileLanguage = useSelector(selectLanguage);

	const dispatch = useDispatch<StoreThunkDispatch>();
	const store = useStore<StoreState>();
	const {history} = useRouter();
	const urlBuilder = useUrlBuilder();
	const {wakeUpDetector} = useContext(ServicesContext)!;

	useEffect(() => {
		let timeoutId: number;
		let onVisibleHandler: (() => void) | undefined;
		let lastFullUpdateTime = Date.now();

		const update = async (fullUpdate: boolean) => {
			if (onVisibleHandler) {
				document.removeEventListener('visibilitychange', onVisibleHandler);
				onVisibleHandler = undefined;
			}

			// If the tab is not visible, postpone update to the tab activate.
			// But if the last update was more than X(in config) time ago, update anyway
			if (
				document.visibilityState === 'hidden' &&
				!(fullUpdate && Date.now() - lastFullUpdateTime > refreshInactiveTabPeriod)
			) {
				onVisibleHandler = () => {
					if (document.visibilityState === 'visible') {
						document.removeEventListener('visibilitychange', onVisibleHandler!);
						onVisibleHandler = undefined;
						clearTimeout(timeoutId);
						update(fullUpdate);
					}
				};
				document.addEventListener('visibilitychange', onVisibleHandler);

				startTimeout();
				return;
			}

			const currentStoreState = store.getState();
			const currentForecast = selectForecastData(currentStoreState)!.forecast;
			const currentNowDate = currentForecast.find(day => day.now)?.date;

			// Ignore update for archived dates
			if (!currentNowDate) {
				return;
			}

			const retryIntervals = [5000, 10000, 20000, 30000, 30000, 30000, 60000, 60000, 60000];

			const fetch = async () => {
				const viewParams = selectForecastViewParams(currentStoreState);
				const hasSelectedDay = !!viewParams.selectedDate || viewParams.selectedWeekday !== undefined;
				const selectedDayDate = hasSelectedDay ? currentForecast[viewParams.selectedDay].date : undefined;

				try {
					await dispatch(
						reFetchForecastDataAction({
							mode: fullUpdate
								? {type: 'all', selectedDate: selectedDayDate}
								: {type: 'currentDay', date: currentNowDate},
						}),
					);
				} catch (error) {
					console.error(error);
					const restartTimeout = retryIntervals.shift();

					// Retry the request
					if (restartTimeout) {
						timeoutId = window.setTimeout(fetch, restartTimeout);
						return;
					}

					startTimeout();
					return;
				}

				startTimeout();

				if (fullUpdate) {
					lastFullUpdateTime = Date.now();
				}

				let actualStoreState = store.getState();
				let actualForecast = selectForecastData(actualStoreState)!.forecast;

				// If a current day changes make the full data update
				if (!fullUpdate && !actualForecast.some(day => day.now)) {
					await dispatch(reFetchForecastDataAction({mode: {type: 'all', selectedDate: selectedDayDate}}));
				}

				actualStoreState = store.getState();
				actualForecast = selectForecastData(actualStoreState)!.forecast;

				// If there is no current day (with "now" parameter) in the list, then the data is outdated (more than ten days from last update)
				// In this case, reset the date and load an actual date
				if (!actualForecast.some(day => day.now)) {
					const url = urlBuilder(RouteName.weather, {location, daysMode: viewParams.daysMode});
					history!.replace(url);
					return;
				}

				// When the selected date was in the future, and now it becomes today's reset date in the route
				if (selectedDayDate && actualForecast.find(item => item.date === selectedDayDate)?.now) {
					const url = urlBuilder(RouteName.weather, {location, daysMode: viewParams.daysMode});
					history!.replace(url);
				}
			};

			return fetch();
		};

		const startTimeout = () => {
			clearTimeout(timeoutId);

			const now = new Date();
			const nowTimeFromHourStart = now.getMinutes() * 60 * 1000 + now.getSeconds() * 1000 + now.getMilliseconds();

			let timeToNextFullUpdate;

			// Calculating the number of minutes until the next full update
			for (const updatePoint of fullRefreshAt) {
				if (updatePoint > nowTimeFromHourStart) {
					timeToNextFullUpdate = updatePoint - nowTimeFromHourStart;
					break;
				}
			}

			// If no a time point is found for updating in the current hour,
			// we take the first update time point for the next hour
			if (timeToNextFullUpdate === undefined) {
				timeToNextFullUpdate = 60 * 60 * 1000 - nowTimeFromHourStart + fullRefreshAt[0];
			}

			// If the next full update should come before the regular update, we start the timer for the full update,
			// otherwise the timer for the regular update of the current weather.
			if (refreshCurrentInterval >= timeToNextFullUpdate) {
				timeoutId = window.setTimeout(update, timeToNextFullUpdate, true);
			} else {
				timeoutId = window.setTimeout(update, refreshCurrentInterval, false);
			}
		};

		// Detect wake-up event and trigger a full update (workaround for safari)
		const disposeWakeUpDetector = wakeUpDetector?.onWakeUp.on(() => {
			update(true);
		});

		startTimeout();

		return () => {
			clearTimeout(timeoutId);
			if (onVisibleHandler) {
				document.removeEventListener('visibilitychange', onVisibleHandler);
			}
			if (disposeWakeUpDetector) {
				disposeWakeUpDetector();
			}
		};
	}, [location.id, profileLanguage]);
}

useWeatherActualize.displayName = 'useWeatherActualize';
